// src/directives/hero-input-password.directive.ts
import { Directive, ElementRef, Renderer2, AfterViewInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const ICON_HIDE = 'hero-icon-users';
const ICON_VIEW = 'hero-icon-view';

@Directive({
  selector: 'input[type="password"].hero-input-password',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HeroInputPasswordDirective),
      multi: true
    }
  ],
  standalone: true,
})
export class HeroInputPasswordDirective implements ControlValueAccessor, AfterViewInit {
  onChange: (value: string) => void = () => {};
  onTouched: () => void = () => {};
  private isPasswordVisible = false;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2
  ) {
    this.setupEventListeners();
  }

  ngAfterViewInit(): void {
    this.buildDomStructure();
  }

  private buildDomStructure(): void {
    const input = this.el.nativeElement;
    const parent = input.parentElement;
    const nextSibling = input.nextSibling;

    // Create wrapper
    const wrapperDiv = this.renderer.createElement('div');
    this.renderer.addClass(wrapperDiv, 'hero-password-wrapper');

    // Create toggle button
    const toggleButton = this.renderer.createElement('button');
    this.renderer.setAttribute(toggleButton, 'type', 'button'); // Prevent form submission
    ['btn', 'btn-hero','btn-hero-password-toggle', ].forEach((className) => {
      this.renderer.addClass(toggleButton, className);
    });

    const icon = this.renderer.createElement('i');
    this.renderer.addClass(icon, ICON_HIDE); // Default: eye (hidden)
    this.renderer.appendChild(toggleButton, icon);

    // Append input and button to wrapper
    this.renderer.appendChild(wrapperDiv, input);
    this.renderer.appendChild(wrapperDiv, toggleButton);

    // Insert wrapper into DOM
    if (parent) {
      if (nextSibling) {
        this.renderer.insertBefore(parent, wrapperDiv, nextSibling);
      } else {
        this.renderer.appendChild(parent, wrapperDiv);
      }
    } else {
      console.warn('HeroInputPasswordDirective: No parent element found for wrapping.');
    }

    // Add click listener to toggle button
    this.renderer.listen(toggleButton, 'click', () => {
      this.togglePasswordVisibility(icon);
    });
  }

  private togglePasswordVisibility(icon: HTMLElement): void {
    const input = this.el.nativeElement;
    this.isPasswordVisible = !this.isPasswordVisible;
    if (this.isPasswordVisible) {
      this.renderer.setAttribute(input, 'type', 'text');
      this.renderer.removeClass(icon, ICON_HIDE);
      this.renderer.addClass(icon, ICON_VIEW);
    } else {
      this.renderer.setAttribute(input, 'type', 'password');
      this.renderer.removeClass(icon, ICON_VIEW);
      this.renderer.addClass(icon, ICON_HIDE);
    }
  }

  private setupEventListeners(): void {
    const input = this.el.nativeElement;
    this.renderer.listen(input, 'input', () => {
      const value = input.value;
      this.onChange(value);
    });
    this.renderer.listen(input, 'blur', () => {
      this.onTouched();
    });
  }

  writeValue(value: string): void {
    this.renderer.setProperty(this.el.nativeElement, 'value', value || '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }
}
